import React, { useState, useRef, useEffect } from 'react';
import { X, Minus, Maximize2, Send, Beer, Briefcase, Trash2, GripHorizontal, ExternalLink } from 'lucide-react';
import { useDEAN } from '../../contexts/DEANContext';

/**
 * DEAN Chat Panel
 *
 * Floating/dockable chat interface for talking to DEAN.
 * - Docked mode: Fixed position bottom-right
 * - Floating mode: Draggable anywhere on screen, resizable
 * - Cubicle mode: Full Dean Winchester personality
 * - Phone mode: Professional documentation voice
 */

// Size constraints
const MIN_WIDTH = 320;
const MIN_HEIGHT = 400;
const MAX_WIDTH = 800;
const MAX_HEIGHT = 900;
const DEFAULT_WIDTH = 380;
const DEFAULT_HEIGHT = 480;

export const DEANPanel: React.FC = () => {
  const {
    isOpen,
    isDocked,
    mode,
    messages,
    isLoading,
    error,
    clientName,
    closePanel,
    toggleDocked,
    setMode,
    sendMessage,
    clearChat
  } = useDEAN();

  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Dragging state for floating mode
  const [position, setPosition] = useState({ x: window.innerWidth - 420, y: window.innerHeight - 520 });
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  // Resize state for floating mode
  const [size, setSize] = useState({ width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT });
  const [isResizing, setIsResizing] = useState(false);
  const resizeStart = useRef({ x: 0, y: 0, width: 0, height: 0 });

  // Minimized state
  const [isMinimized, setIsMinimized] = useState(false);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Handle dragging
  const handleMouseDown = (e: React.MouseEvent) => {
    if (isDocked) return;
    setIsDragging(true);
    dragOffset.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - size.width, e.clientX - dragOffset.current.x)),
        y: Math.max(0, Math.min(window.innerHeight - size.height, e.clientY - dragOffset.current.y))
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, size.width, size.height]);

  // Handle resize
  const handleResizeMouseDown = (e: React.MouseEvent) => {
    if (isDocked) return;
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    resizeStart.current = {
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height
    };
  };

  useEffect(() => {
    const handleResizeMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const deltaX = e.clientX - resizeStart.current.x;
      const deltaY = e.clientY - resizeStart.current.y;

      const newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, resizeStart.current.width + deltaX));
      const newHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, resizeStart.current.height + deltaY));

      setSize({ width: newWidth, height: newHeight });
    };

    const handleResizeUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleResizeMove);
      window.removeEventListener('mouseup', handleResizeUp);
    };
  }, [isResizing]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;
    sendMessage(inputValue);
    setInputValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCloseSession();
    }
  };

  // Handle Enter (send) vs Shift+Enter (new line) in textarea
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (inputValue.trim() && !isLoading) {
        sendMessage(inputValue);
        setInputValue('');
        // Reset textarea height after sending
        if (inputRef.current) {
          inputRef.current.style.height = 'auto';
        }
      }
    }
  };

  // Auto-resize textarea as content grows
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    // Auto-resize: reset height then set to scrollHeight
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 150) + 'px'; // Max 150px (~6 lines)
  };

  // Close and clear session completely
  const handleCloseSession = () => {
    clearChat();
    setIsMinimized(false);
    closePanel();
  };

  if (!isOpen) return null;

  const panelStyle: React.CSSProperties = isDocked
    ? { bottom: 24, right: 100, width: DEFAULT_WIDTH, height: isMinimized ? 'auto' : DEFAULT_HEIGHT }
    : { left: position.x, top: position.y, width: size.width, height: isMinimized ? 'auto' : size.height };

  return (
    <>
      <div
        className={`dean-panel ${isDocked ? 'docked' : 'floating'} ${isDragging ? 'dragging' : ''} ${isResizing ? 'resizing' : ''} ${isMinimized ? 'minimized' : ''}`}
        style={panelStyle}
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div
          className="dean-header"
          onMouseDown={handleMouseDown}
        >
          <div className="dean-title">
            {mode === 'cubicle' ? <Beer size={18} /> : <Briefcase size={18} />}
            <span>DEAN</span>
          </div>
          <div className="dean-controls">
            {/* Mode toggle */}
            <button
              className={`mode-btn ${mode === 'cubicle' ? 'active' : ''}`}
              onClick={() => setMode('cubicle')}
              title="Cubicle Mode - Full personality"
            >
              <Beer size={14} />
            </button>
            <button
              className={`mode-btn ${mode === 'phone' ? 'active' : ''}`}
              onClick={() => setMode('phone')}
              title="Phone Mode - Professional"
            >
              <Briefcase size={14} />
            </button>
            <div className="control-divider" />
            {/* Dock/Float toggle */}
            <button
              className="control-btn"
              onClick={toggleDocked}
              title={isDocked ? 'Pop out panel' : 'Dock panel'}
            >
              {isDocked ? <ExternalLink size={14} /> : <Maximize2 size={14} />}
            </button>
            {/* Minimize */}
            <button
              className="control-btn"
              onClick={() => setIsMinimized(!isMinimized)}
              title={isMinimized ? 'Expand' : 'Minimize'}
            >
              {isMinimized ? <Maximize2 size={14} /> : <Minus size={14} />}
            </button>
            {/* Clear chat */}
            <button
              className="control-btn"
              onClick={clearChat}
              title="Clear conversation"
            >
              <Trash2 size={14} />
            </button>
            {/* Close - ends session */}
            <button className="control-btn close-btn" onClick={handleCloseSession} title="Close session">
              <X size={14} />
            </button>
          </div>
          {!isDocked && !isMinimized && (
            <div className="drag-indicator">
              <GripHorizontal size={14} />
            </div>
          )}
        </div>

        {/* Messages and Input - hidden when minimized */}
        {!isMinimized && (
        <>
        <div className="dean-messages">
          {messages.length === 0 && (
            <div className="dean-welcome">
              {mode === 'cubicle' ? (
                <>
                  <div className="welcome-icon"><Beer size={32} /></div>
                  <p>Hey there, champ. What do you need to know about {clientName || 'your client'}?</p>
                  <p className="welcome-hint">Ask me anything - metrics, trends, what to focus on in your next session.</p>
                </>
              ) : (
                <>
                  <div className="welcome-icon"><Briefcase size={32} /></div>
                  <p>DEAN Professional Mode</p>
                  <p className="welcome-hint">How can I assist with documentation or analysis?</p>
                </>
              )}
            </div>
          )}

          {messages.map((msg) => (
            <div key={msg.id} className={`message ${msg.role}`}>
              <div className="message-content">
                {msg.content.split('\n').map((line, i) => (
                  <React.Fragment key={i}>
                    {line}
                    {i < msg.content.split('\n').length - 1 && <br />}
                  </React.Fragment>
                ))}
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="message dean loading">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}

          {error && (
            <div className="message error">
              <div className="message-content">{error}</div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form className="dean-input" onSubmit={handleSubmit}>
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            placeholder={mode === 'cubicle' ? "Ask DEAN anything..." : "Request documentation assistance..."}
            disabled={isLoading}
            rows={1}
          />
          <button type="submit" disabled={!inputValue.trim() || isLoading}>
            <Send size={18} />
          </button>
        </form>
        </>
        )}

        {/* Resize handle - only in floating mode, not when minimized */}
        {!isDocked && !isMinimized && (
          <div
            className="resize-handle"
            onMouseDown={handleResizeMouseDown}
            title="Drag to resize"
          />
        )}
      </div>

      <style>{`
        .dean-panel {
          position: fixed;
          width: 380px;
          height: 480px;
          background: var(--bg-primary);
          border: 2px solid var(--accent-blue);
          border-radius: 12px;
          box-shadow: 0 8px 32px rgba(59, 130, 246, 0.15), 0 4px 16px rgba(0, 0, 0, 0.2);
          display: flex;
          flex-direction: column;
          z-index: 1000;
          overflow: hidden;
        }

        .dean-panel.docked {
          position: fixed;
        }

        .dean-panel.floating {
          position: fixed;
        }

        .dean-panel.dragging {
          cursor: grabbing;
          user-select: none;
        }

        .dean-panel.resizing {
          user-select: none;
        }

        .resize-handle {
          position: absolute;
          right: 0;
          bottom: 0;
          width: 16px;
          height: 16px;
          cursor: se-resize;
          background: linear-gradient(
            135deg,
            transparent 50%,
            var(--text-muted) 50%,
            var(--text-muted) 60%,
            transparent 60%,
            transparent 70%,
            var(--text-muted) 70%,
            var(--text-muted) 80%,
            transparent 80%
          );
          opacity: 0.5;
          transition: opacity 0.15s;
          border-radius: 0 0 12px 0;
          z-index: 10;
        }

        .resize-handle:hover {
          opacity: 0.8;
        }

        .dean-panel.resizing .resize-handle {
          opacity: 1;
        }

        .dean-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          background: var(--bg-secondary);
          border-bottom: 1px solid var(--border-primary);
          cursor: ${props => props.isDocked ? 'default' : 'grab'};
          position: relative;
        }

        .dean-panel.floating .dean-header {
          cursor: grab;
        }

        .dean-panel.dragging .dean-header {
          cursor: grabbing;
        }

        .dean-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .dean-title svg {
          color: var(--accent-green);
        }

        .dean-controls {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .mode-btn {
          padding: 6px 8px;
          background: transparent;
          border: 1px solid var(--border-secondary);
          border-radius: 6px;
          color: var(--text-muted);
          cursor: pointer;
          transition: all 0.15s;
        }

        .mode-btn:hover {
          background: var(--bg-tertiary);
          color: var(--text-primary);
        }

        .mode-btn.active {
          background: var(--accent-green);
          border-color: var(--accent-green);
          color: white;
        }

        .control-divider {
          width: 1px;
          height: 20px;
          background: var(--border-secondary);
          margin: 0 4px;
        }

        .control-btn {
          padding: 6px;
          background: transparent;
          border: none;
          border-radius: 6px;
          color: var(--text-muted);
          cursor: pointer;
          transition: all 0.15s;
        }

        .control-btn:hover {
          background: var(--bg-tertiary);
          color: var(--text-primary);
        }

        .control-btn.close-btn:hover {
          background: var(--accent-red, #e53e3e);
          color: white;
        }

        .drag-indicator {
          position: absolute;
          bottom: -2px;
          left: 50%;
          transform: translateX(-50%);
          color: var(--text-muted);
          opacity: 0.5;
        }

        .dean-messages {
          flex: 1;
          overflow-y: auto;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .dean-welcome {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          height: 100%;
          color: var(--text-secondary);
          padding: 20px;
        }

        .welcome-icon {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: var(--bg-tertiary);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 16px;
          color: var(--accent-green);
        }

        .welcome-hint {
          font-size: 13px;
          color: var(--text-muted);
          margin-top: 8px;
        }

        .message {
          max-width: 85%;
          padding: 10px 14px;
          border-radius: 12px;
          font-size: 14px;
          line-height: 1.5;
        }

        .message.coach {
          align-self: flex-end;
          background: var(--accent-green);
          color: white;
          border-bottom-right-radius: 4px;
        }

        .message.dean {
          align-self: flex-start;
          background: var(--bg-secondary);
          color: var(--text-primary);
          border-bottom-left-radius: 4px;
        }

        .message.error {
          align-self: center;
          background: var(--accent-red, #e53e3e);
          color: white;
          font-size: 13px;
        }

        .message.loading {
          padding: 16px 20px;
        }

        .typing-indicator {
          display: flex;
          gap: 4px;
        }

        .typing-indicator span {
          width: 8px;
          height: 8px;
          background: var(--text-muted);
          border-radius: 50%;
          animation: typing 1.4s infinite ease-in-out both;
        }

        .typing-indicator span:nth-child(1) {
          animation-delay: -0.32s;
        }

        .typing-indicator span:nth-child(2) {
          animation-delay: -0.16s;
        }

        @keyframes typing {
          0%, 80%, 100% {
            transform: scale(0.6);
            opacity: 0.4;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }

        .dean-input {
          display: flex;
          align-items: flex-end;
          gap: 8px;
          padding: 12px 16px;
          border-top: 1px solid var(--border-primary);
          background: var(--bg-secondary);
        }

        .dean-input textarea {
          flex: 1;
          padding: 10px 14px;
          border: 1px solid var(--border-secondary);
          border-radius: 8px;
          background: var(--bg-primary);
          color: var(--text-primary);
          font-size: 14px;
          line-height: 1.4;
          resize: none;
          min-height: 40px;
          max-height: 150px;
          overflow-y: auto;
          font-family: inherit;
        }

        .dean-input textarea:focus {
          outline: none;
          border-color: var(--accent-green);
        }

        .dean-input textarea::placeholder {
          color: var(--text-muted);
        }

        .dean-input textarea:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .dean-input button {
          padding: 10px 14px;
          background: var(--accent-green);
          border: none;
          border-radius: 8px;
          color: white;
          cursor: pointer;
          transition: all 0.15s;
        }

        .dean-input button:hover:not(:disabled) {
          background: #059669;
        }

        .dean-input button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>
    </>
  );
};
